import React from 'react';

const Story = () => {
  return (
    <article className="prose prose-lg sm:prose-xl text-slate-700 max-w-none mb-12">
      <div className="mb-10 relative">
        <h3 className="text-lg font-bold text-slate-800 mb-3 ml-1">Watch my quick 60-second review:</h3>
        <video 
          controls 
          className="w-full rounded-2xl shadow-lg border border-slate-100 bg-black aspect-video object-contain"
          poster="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvcG5n/ea72968f8a3f683b4a4bee26fef09f8e.png"
        >
          <source src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/videos/dmlkZW8vcXVpY2t0aW1l/baa131c89b7367ad78e2617fca66c08a.mov" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      <p className="text-xl leading-relaxed mb-6">
        Hi, I'm Nancy, a 66-year-old church choir singer. I've always loved singing and going to social gatherings, but after turning 55, I started noticing my energy dropping significantly by mid-afternoon no matter what I tried.
      </p>

      <div className="bg-slate-50 border-l-4 border-rose-400 p-6 rounded-r-lg mb-8 mt-8">
        <p className="m-0 italic">
          "It made simple daily tasks feel harder and left me feeling less present during choir practice and get-togethers. I tried different options but nothing seemed to give me consistent support all day."
        </p>
      </div>

      <p className="mb-6">
        I was frustrated. I felt like I was doing everything "right"—drinking water, trying to get enough sleep—but that afternoon crash was inevitable. I needed something that worked with my body, not just another quick fix that led to a harder crash later.
      </p>

      <figure className="my-8">
        <img 
          src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvcG5n/a8da0fb459d6bc0cb9144ead2bfc5a76.png" 
          alt="Feeling exhausted during choir practice" 
          className="w-full max-w-md mx-auto rounded-2xl shadow-md h-auto block" 
        />
        <figcaption className="text-center text-sm text-slate-500 mt-3 italic">
          Struggling to stay awake and present during afternoon choir practice.
        </figcaption>
      </figure>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 mt-12">The Turning Point</h2>

      <p className="mb-6">
        Then I discovered <strong>CitrusBurn</strong> — a 100% plant-based capsule with a unique blend of <strong>6 natural Spanish botanicals</strong> designed to support healthy metabolism and energy levels.
      </p>

      <figure className="my-8">
        <img 
          src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvcG5n/82b7c1f708a3c43c72fd040ff89e1767.png" 
          alt="Trying CitrusBurn supplement" 
          className="w-full max-w-md mx-auto rounded-2xl shadow-md h-auto block" 
        />
        <figcaption className="text-center text-sm text-slate-500 mt-3 italic">
          Finally finding something that actually works with my body.
        </figcaption>
      </figure>

      {/* Updated Ingredients Section */}
      <div className="mb-12 mt-12">
        <h3 className="text-2xl font-bold text-slate-900 mb-4">The Secret: 6 Powerful Natural Botanicals</h3>
        <p className="mb-6 leading-relaxed">
          What really caught my attention was the ingredient list. Instead of relying on harsh, jitter-inducing stimulants, CitrusBurn uses a carefully crafted blend of <strong>6 natural ingredients</strong> designed to work in harmony with your body.
        </p>

        <figure className="my-8">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvanBlZw==/b835a1d10f09cbd8a5c9336ba7ef2fd9.jpeg" 
            alt="6 Natural Botanicals in CitrusBurn" 
            className="w-full max-w-lg mx-auto rounded-2xl shadow-md h-auto block border border-slate-100" 
          />
          <figcaption className="text-center text-sm text-slate-500 mt-3 italic">
            A synergistic blend of 6 natural ingredients.
          </figcaption>
        </figure>

        <p className="mb-6 leading-relaxed">
          The absolute standout of this blend is the <strong>Seville Orange Peel (p-synephrine)</strong>. This remarkable natural compound is the primary driver that supports thermogenesis and helps the body burn fat efficiently. When combined with other metabolism-supporting ingredients like Spanish Red Apple Vinegar, Andalusian Red Pepper, and Ceremonial Green Tea, it creates a powerful, sustained energy lift without the dreaded afternoon crash.
        </p>
      </div>

      {/* Mechanism Section */}
      <div className="bg-amber-50 border border-amber-100 p-6 sm:p-8 rounded-3xl my-10 shadow-sm relative">
        <h3 className="text-2xl font-bold text-amber-900 mb-4 mt-0">The Science: What Is Thermogenesis?</h3>
        
        <p className="text-amber-800 mb-4 leading-relaxed">
          Simply put, thermogenesis is the internal engine your body uses to convert calories into usable energy. It's the silent force driving everything from your daily movements and digestion to maintaining your core body temperature and processing fats.
        </p>

        <figure className="my-8">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvcG5n/8ce63f99d9f7c2aef472f1b3165f3228.png" 
            alt="Thermogenesis decline with age" 
            className="w-full max-w-md mx-auto rounded-xl shadow-md h-auto block" 
          />
          <figcaption className="text-center text-xs text-amber-700/70 mt-3 italic">
            The stark difference in cellular thermogenesis as we age.
          </figcaption>
        </figure>

        <p className="text-amber-800 mb-4 leading-relaxed">
          Unfortunately, as we cross the age of 55, this internal furnace naturally begins to cool down—a shift that hits women particularly hard. This biological slowdown is frequently the root cause behind stubborn weight gain, that familiar midday exhaustion, and hitting a plateau despite your best efforts.
        </p>

        <p className="text-amber-800 mb-6 leading-relaxed">
          But here is the exciting part: CitrusBurn™ was specifically developed to target this exact issue. By utilizing a precisely measured blend of scientifically-backed botanicals, it helps reignite your natural thermogenic process at the cellular level, effectively breaking the cycle of a sluggish metabolism.
        </p>

        <div className="bg-white/60 border-l-4 border-amber-400 p-5 rounded-r-lg">
          <p className="m-0 text-amber-900 font-semibold italic">
            "Once you successfully reset your metabolic rate, your body becomes naturally more efficient at burning calories around the clock, regardless of what you eat."
          </p>
        </div>
      </div>

      <div className="bg-teal-50 border border-teal-100 p-6 sm:p-8 rounded-2xl mb-8 mt-10">
        <h3 className="text-2xl font-bold text-teal-900 mb-4 mt-0">My Results Today</h3>
        
        <figure className="mb-6">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/bf058984a4ffdbb1bcf737e98ba89b4a0e9a3794368100afc61d724fc2136ffb549bddff932effd18582b107d1ceb382/images/aW1hZ2UvcG5n/9aeb1b3b97d208e9dde614741680c82b.png" 
            alt="Happy and energized at choir practice" 
            className="w-full max-w-md mx-auto rounded-xl shadow-sm h-auto block" 
          />
        </figure>

        <p className="m-0 text-teal-900 font-medium leading-relaxed">
          Now I enjoy steady energy consistency throughout the day. I have the stamina I need for choir practice and love staying active with my friends in the evenings. It has helped me feel so much more vibrant overall. I actually have the energy to do the things I love again!
        </p>
      </div>
    </article>
  );
};

export default Story;